import { useState, type FormEvent } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { Lock, Plus } from "lucide-react";
import type { RestaurantDto } from "@dishfolio/contracts";
import { Button, Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@dishfolio/ui";
import { api } from "../lib/api.ts";
import { useOrg } from "../lib/org.tsx";
import { useRestaurants } from "../lib/queries.ts";
import { RestaurantFields, type RestaurantDraft } from "../pages/onboarding.tsx";
import { notifyError, UpgradeCard } from "./Upgrade.tsx";

/** "Add location" for organizations with several restaurants (Business: up to 10). */
export function NewLocationButton({ onNavigate }: { onNavigate?: () => void }) {
  const { org, can } = useOrg();
  const restaurants = useRestaurants();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const first = restaurants.data?.[0];
  const [draft, setDraft] = useState<RestaurantDraft>({
    name: "",
    address: "",
    defaultLanguage: first?.defaultLanguage ?? "en",
    currency: first?.currency ?? "EUR",
    timezone: first?.timezone ?? (Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC"),
  });
  if (!org || !can("restaurant:manage")) return null;
  const used = restaurants.data?.length ?? 0;
  const max = org.plan.maxRestaurants;
  const atLimit = used >= max;
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.post<{ restaurant: RestaurantDto }>("/restaurants", draft);
      await qc.invalidateQueries({ queryKey: ["restaurants"] });
      setOpen(false);
      onNavigate?.();
      await navigate({ to: "/restaurants/$restaurantId", params: { restaurantId: res.restaurant.id } });
    } catch (err) {
      notifyError(err);
    } finally {
      setLoading(false);
    }
  };
  return (
    <>
      <button
        type="button"
        onClick={() => {
          // Default to the first location's settings (most groups share currency and time zone).
          if (first) setDraft((d) => ({ ...d, defaultLanguage: first.defaultLanguage, currency: first.currency, timezone: first.timezone }));
          setOpen(true);
        }}
        className="flex items-center gap-2.5 rounded-md px-2.5 py-1.5 text-sm font-medium text-muted-foreground transition hover:bg-accent hover:text-foreground [&_svg]:size-4"
      >
        {atLimit ? <Lock /> : <Plus />} Add location
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Add a location</DialogTitle>
          </DialogHeader>
          {atLimit ? (
            <UpgradeCard
              plan="Business"
              title={max === 1 ? "Multiple locations" : `You’re using all ${max} locations`}
              description={max === 1 ? "Business lets you manage up to 10 restaurants, each with its own menus, currency, taxes, tables and custom domain." : "Contact us if you need more locations."}
            />
          ) : (
            <form onSubmit={submit} className="grid gap-5">
              <p className="text-sm text-muted-foreground">
                Each location has its own menus, address, currency and taxes{org.plan.tableOrdering ? ", tables and order board" : ""}. {used} of {max} used.
              </p>
              <RestaurantFields value={draft} onChange={setDraft} />
              <DialogFooter>
                <Button type="button" variant="ghost" onClick={() => setOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" loading={loading}>
                  Create location
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
